import apiClient from '@utils/apiClient';
import { config } from '@utils/index';
import {
  EeoiFleet,
  PerformanceFleet,
  VesselPerformance,
  VesselsNames,
  Voyages,
  PerformanceVoyage,
  GetFleetRatingsParams,
  CiiFleetRatings,
} from './apiGateway.dto';

const apiGatewayBaseURL = config?.services?.apiGateway;
export const apiGatewayAPI = apiClient(apiGatewayBaseURL);

/**
 * Retrieves the EEOI fleet scores for a client within a specified date range.
 *
 * @async
 * @function getEeoiFleetScores
 * @param {string} startDate - The start date of the date range to retrieve fleet scores for.
 * @param {string} endDate - The end date of the date range to retrieve fleet scores for.
 * @returns {Promise<EeoiFleet>} A promise that resolves with the fleet scores data.
 */
export const getEeoiFleetScores = async (startDate: string, endDate: string, enableCache = true) =>
  apiGatewayAPI.get<EeoiFleet>(`fleet/eeoi`, {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
    headers: enableCache ? {} : { 'Cache-Control': 'max-age=300' },
  });

/**
 * Retrieves the names of vessels in the fleet.
 *
 * @async
 * @function getVessels
 * @returns {Promise<VesselsNames>} A promise that resolves with the vessel names data.
 */
export const getVessels = async () => apiGatewayAPI.get<VesselsNames>(`fleet/vessels_name`);

/**
 * Retrieves fleet ratings data for a given client ID and year.
 *
 * @async
 * @function getFleetRatings
 * @param {GetFleetRatingsParams} params - The parameters for retrieving fleet ratings data.
 * @returns {Promise<CiiFleetRatingsResponse>} A promise that resolves with the fleet ratings data.
 */
export const getFleetRatings = async ({
  startDate,
  endDate,
  clientTimezoneOffset,
  isDataExport = false,
}: GetFleetRatingsParams) =>
  apiGatewayAPI.get<CiiFleetRatings>(`fleet/cii`, {
    params: {
      startDate,
      endDate,
      clientTimezoneOffset,
      isDataExport,
    },
  });

/**
 * Retrieves voyages data for a given IMO number.
 *
 * @async
 * @function getVoyages
 * @param {number} imo - The IMO number of the vessel to retrieve voyages data for.
 * @returns {Promise<Voyages>} A promise that resolves with the voyages data.
 */
export const getVoyages = (imo: number) => apiGatewayAPI.get<Voyages>(`performance/fleet/${imo}/voyages`);

/**
 * Retrieves performance fleet data.
 *
 * @async
 * @function getPerformanceFleet
 * @returns {Promise<PerformanceFleet>} A promise that resolves with the performance fleet.
 */
export const getPerformanceFleet = async () => apiGatewayAPI.get<PerformanceFleet>(`performance/fleet`);

/**
 * Retrieves performance vessel data for a given IMO number.
 *
 * @async
 * @function getPerformanceVessel
 * @param {number} imo - The IMO number of the vessel to retrieve performance data for.
 * @returns {Promise<VesselPerformance>} A promise that resolves with the performance vessel data.
 */
export const getPerformanceVessel = async (imo: number) =>
  apiGatewayAPI.get<VesselPerformance>(`performance/fleet/${imo}`);

/**
 * Retrieves performance voyage data for a given IMO number and voyage ID.
 *
 * @async
 * @function getPerformanceVoyage
 * @param {number} imo - The IMO number of the vessel associated with the voyage.
 * @param {string} voyageId - The ID of the voyage to retrieve performance data for.
 * @returns {Promise<PerformanceVoyage>} A promise that resolves with the performance voyage data.
 */
export const getPerformanceVoyage = async (imo: number, voyageId: string) =>
  apiGatewayAPI.get<PerformanceVoyage>(`performance/fleet/${imo}/voyages/${voyageId}`);
